import { safeBlogHtml } from "../../utils/safeBlogHtml";
import { displayLabel } from "../../utils/displayLabel";
import React, { useState, useEffect, useMemo, useRef } from "react";
import { createBlogApi } from "./blogApi";
import { useEditor, EditorContent } from "@tiptap/react";
import { mergeAttributes } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";
import BulletList from "@tiptap/extension-bullet-list";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import TextAlign from "@tiptap/extension-text-align";
import Image from "@tiptap/extension-image";
import {
  getAnchorAttributesForHref,
  normalizeBlogContentHtml,
  validBlogLink,
} from "./blogLinkUtils";

const toolbarButtonClass = (isActive) =>
  [
    "min-h-[44px] min-w-[2.5rem] rounded-md border px-3 py-1.5 text-sm font-semibold transition",
    isActive
      ? "border-bookish-purple/40 bg-bookish-purple/20 text-bookish-purple shadow-sm"
      : "border-bookish-purple/15 bg-white/90 text-bookish-purple/80 hover:bg-bookish-purple/10",
  ].join(" ");

const getImageAlignmentStyles = (align) => {
  if (align === "left") {
    return "display:block;max-width:min(100%, 480px);width:auto;height:auto;margin:1rem auto 1rem 0;";
  }

  if (align === "right") {
    return "display:block;max-width:min(100%, 480px);width:auto;height:auto;margin:1rem 0 1rem auto;";
  }

  return "display:block;max-width:min(100%, 480px);width:auto;height:auto;margin:1rem auto;";
};

const StyledBulletList = BulletList.extend({
  addAttributes() {
    return {
      ...(this.parent?.() ?? {}),
      listStyle: {
        default: "disc",
        parseHTML: (element) => element.getAttribute("data-list-style") || "disc",
        renderHTML: (attributes) => ({
          "data-list-style": attributes.listStyle,
        }),
      },
    };
  },

  renderHTML({ HTMLAttributes }) {
    return ["ul", mergeAttributes(this.options.HTMLAttributes, HTMLAttributes), 0];
  },
});

const AlignableImage = Image.extend({
  addAttributes() {
    return {
      ...(this.parent?.() ?? {}),
      align: {
        default: "center",
        parseHTML: (element) => element.getAttribute("data-align") || "center",
        renderHTML: (attributes) => ({
          "data-align": attributes.align,
        }),
      },
      linkHref: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-link-href") || null,
        renderHTML: (attributes) =>
          attributes.linkHref
            ? { "data-link-href": attributes.linkHref }
            : {},
      },
    };
  },

  renderHTML({ HTMLAttributes }) {
    const { "data-align": align = "center", "data-link-href": linkHref, ...rest } = HTMLAttributes;
    const imageNode = [
      "img",
      mergeAttributes(rest, {
        style: getImageAlignmentStyles(align),
      }),
    ];

    if (!linkHref) {
      return imageNode;
    }

    const anchorMargins =
      align === "left"
        ? "margin:1rem auto 1rem 0;"
        : align === "right"
          ? "margin:1rem 0 1rem auto;"
          : "margin:1rem auto;";

    return [
      "a",
      mergeAttributes(
        getAnchorAttributesForHref(linkHref),
        {
          "data-link-href": linkHref,
          style: `display:block;width:fit-content;max-width:100%;${anchorMargins}`,
        },
      ),
      [
        "img",
        mergeAttributes(rest, {
          "data-link-href": linkHref,
          style: "display:block;max-width:min(100%, 480px);width:auto;height:auto;margin:0;",
        }),
      ],
    ];
  },
});

const BlogLink = Link.extend({
  renderHTML({ HTMLAttributes }) {
    const { href, target: _target, rel: _rel, ...rest } = HTMLAttributes;

    return [
      "a",
      mergeAttributes(this.options.HTMLAttributes, rest, getAnchorAttributesForHref(href)),
      0,
    ];
  },
});

const freshPost = () => ({ id: crypto.randomUUID(), title: '', content: '', isPublished: false, version: 'new' });
const editable = post => JSON.stringify([post.title, /^\s*(?:<p>\s*(?:<br\s*\/?>)?\s*<\/p>)?\s*$/i.test(post.content || '') ? '' : post.content]);
const excerpt = content => { const doc = new DOMParser().parseFromString(safeBlogHtml(content), 'text/html'); return doc.body.textContent.slice(0, 160); };
const Blog = ({ contentApi, groupPublished }) => {
  const api = useMemo(() => createBlogApi(contentApi), [contentApi]);
  const [posts, setPosts] = useState([]), [pending, setPending] = useState([]);
  const [post, setPost] = useState(freshPost), [editing, setEditing] = useState(false);
  const [saved, setSaved] = useState(JSON.stringify(['','']));
  const [ready, setReady] = useState(false), [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(''), [error, setError] = useState(''), [message, setMessage] = useState('');
  const [retry, setRetry] = useState(0), [draftsOpen, setDraftsOpen] = useState(true), [yearsOpen, setYearsOpen] = useState({});
  const [monthsOpen, setMonthsOpen] = useState({});
  const [conflict, setConflict] = useState(false);
  const alive = useRef(false);
  useEffect(()=>{alive.current=true;return()=>{alive.current=false;};},[]);
  const lock = useRef(false), errorRef = useRef(null);
  const dirty = ready && editable(post) !== saved;
  const editor = useEditor({
    extensions: [StarterKit.configure({ bulletList: false, link: false, underline: false }), StyledBulletList, Underline, BlogLink.configure({ openOnClick: false, autolink: true, defaultProtocol: 'https' }), TextAlign.configure({ types: ['heading','paragraph'] }), AlignableImage.configure({ inline: false })],
    content: '',
    editorProps: { attributes: { role: 'textbox', 'aria-label': 'Post content', 'aria-multiline': 'true', class: 'min-h-[16rem] focus:outline-none prose prose-headings:font-serif max-w-none prose-img:my-4' } },
    onUpdate: ({ editor: current }) => { setPost(previous => ({ ...previous, content: current.getHTML() })); setMessage(''); },
  });
  useEffect(() => { editor?.setEditable(ready && !busy); }, [editor, ready, busy]);
  useEffect(() => { if (error) errorRef.current?.focus(); }, [error]);
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true); setError(''); setReady(false);
    api.list(controller.signal).then(data => { if (!controller.signal.aborted) { setPosts(data.posts); setPending(data.pending); setReady(true); } }).catch(e => { if (!controller.signal.aborted) setError(e.message); }).finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [api, retry]);
  useEffect(() => {
    if (!dirty && !busy) return;
    const unload = e => { e.preventDefault(); e.returnValue = ''; };
    const navigate = e => { if (lock.current || (dirty && !window.confirm('Leave this tab and discard your unsaved Blog edits?'))) e.preventDefault(); };
    window.addEventListener('beforeunload', unload); window.addEventListener('bookish:before-navigation', navigate);
    return () => { window.removeEventListener('beforeunload', unload); window.removeEventListener('bookish:before-navigation', navigate); };
  }, [dirty, busy]);
  function select(next, isEditing = true) {
    if (lock.current || (dirty && !window.confirm('Discard your unsaved Blog edits?'))) return;
    setPost(next); setEditing(isEditing); setSaved(editable(next)); setConflict(false); setError(''); setMessage('');
    editor?.commands.setContent(safeBlogHtml(next.content), { emitUpdate: false });
  }
  async function refresh() {
    const data = await api.list(); if(!alive.current)return; setPosts(data.posts); setPending(data.pending);
  }
  async function run(label, operation) {
    if (lock.current || !ready) return;
    lock.current = true; setBusy(label); setError(''); setMessage('');
    try { await operation(); } catch (e) { if(!alive.current)return; setError(e.message || 'We could not complete this step. Your edits are still here.'); setConflict(e.status === 409); }
    finally { lock.current = false; if(alive.current)setBusy(''); }
  }
  function save(publish) {
    if (!post.title.trim() || post.title.length > 200) { setError('Enter a post title up to 200 characters.'); return; }
    const content = normalizeBlogContentHtml(safeBlogHtml(post.content));
    if (new Blob([content]).size > 250000) { setError('Please shorten this post to under 250 KB.'); return; }
    if (publish && !excerpt(content).trim() && !/<img[^>]+src=/i.test(content)) { setError('Add some content before publishing.'); return; }
    run('Saving post…', async () => {
      const result = await api.save({ ...post, content, isPublished: publish, createdAt: post.createdAt || new Date().toISOString() });
      if(!alive.current)return;
      const confirmed = result.post;
      setPost(confirmed); setSaved(editable(confirmed)); setEditing(true); setConflict(false);
      editor?.commands.setContent(safeBlogHtml(confirmed.content), { emitUpdate: false });
      setPosts(previous => [confirmed, ...previous.filter(item => String(item.id) !== String(confirmed.id))]);
      if (result.cleanupPending) setPending(previous => [...previous.filter(item => item.id !== confirmed.id), { id: confirmed.id, version: confirmed.version, title: confirmed.title }]);
      setMessage(result.cleanupPending ? 'Post saved. Finish the pending update below to remove older public copies.' : publish ? (groupPublished === false ? 'Post saved as published. Readers can see it when your group is published.' : 'Your post is published.') : 'Your post is saved as a draft.');
    });
  }
  function remove(postToRemove) {
    if (lock.current || !window.confirm('Delete "' + postToRemove.title + '"? This cannot be undone.')) return;
    run('Deleting post…', async () => {
      const result = await api.remove(postToRemove);
      if(!alive.current)return;
      setPosts(previous => previous.filter(item => item.id !== postToRemove.id));
      if (String(post.id) === String(postToRemove.id)) { const next = freshPost(); setPost(next); setEditing(false); setSaved(editable(next)); editor?.commands.setContent('', { emitUpdate: false }); }
      if (result.cleanupPending) setPending(previous => [...previous.filter(item => item.id !== postToRemove.id), { id: String(postToRemove.id), version: result.version, title: postToRemove.title }]);
      setMessage(result.cleanupPending ? 'The post is hidden from your blog. Finish the pending update below to remove older public copies.' : 'Post deleted.');
    });
  }
  function upload(file) {
    if (!file || lock.current) return;
    run('Uploading image…', async () => {
      const url = await api.upload(file);
      if(!alive.current)return;
      editor?.chain().focus().setImage({ src: url, align: 'center', alt: '' }).run();
      setMessage('Image uploaded. Add an image description, then save your post.');
    });
  }
  function imageDescription() {
    if (!editor?.isActive('image')) { setError('Select an image in your post first.'); return; }
    const alt = window.prompt('Describe this image for readers using a screen reader. Leave blank only if it is decorative.', editor.getAttributes('image').alt || '');
    if (alt !== null) editor.chain().focus().updateAttributes('image', { alt: alt.slice(0,300) }).run();
  }
  function setLink(image = false) {
    if (image && !editor?.isActive('image')) { setError('Select an image in your post first.'); return; }
    const current = image ? editor.getAttributes('image').linkHref : editor?.getAttributes('link').href;
    const href = window.prompt('Enter a website or email link. Leave blank to remove it.', current || '');
    if (href === null) return;
    if (href.trim() && !validBlogLink(href)) { setError('Use an http://, https://, email, or relative website link.'); return; }
    if (image) editor.chain().focus().updateAttributes('image', { linkHref: href.trim() || null }).run();
    else if (href.trim()) editor?.chain().focus().extendMarkRange('link').setLink({ href: href.trim() }).run();
    else editor?.chain().focus().unsetLink().run();
  }
  const drafts = posts.filter(p => !p.isPublished);
  const published = posts.filter(p => p.isPublished).sort((a,b) => (Date.parse(b.createdAt) || 0) - (Date.parse(a.createdAt) || 0));
  const groups = published.reduce((result, item) => { const key = Number.isFinite(Date.parse(item.createdAt)) ? String(new Date(item.createdAt).getFullYear()) : 'Undated'; const month = Number.isFinite(Date.parse(item.createdAt)) ? String(new Date(item.createdAt).getMonth() + 1).padStart(2, '0') : 'undated'; ((result[key] ||= {})[month] ||= []).push(item); return result; }, {});
  const list = items => <ul className="space-y-4">{items.map(item => <li key={item.id} className="border border-bookish-purple/20 p-4 rounded-md bg-bookish-cream/50"><h3 className="font-serif font-semibold">{displayLabel(item.title) || 'Untitled draft'}</h3><p className="text-sm text-bookish-purple/70 whitespace-pre-wrap">{excerpt(item.content)}</p><div className="flex gap-4"><button type="button" className="underline min-h-[44px]" onClick={() => select(item)} aria-label={'Edit ' + (item.title || 'Untitled draft')}>Edit</button><button type="button" className="underline min-h-[44px]" onClick={() => remove(item)} aria-label={'Delete ' + (item.title || 'Untitled draft')}>Delete</button></div></li>)}</ul>;
  const tool = (label, active, action, text = label) => <button type="button" aria-label={label} aria-pressed={!!active} className={toolbarButtonClass(active)} onClick={action}>{text}</button>;
  return <section className="bg-bookish-cream/40 mt-10 border border-bookish-purple/20 p-4 sm:p-6 rounded-2xl shadow-sm text-bookish-purple">
    <style>{`.blog-editor-content ul[data-list-style="diamond"] {list-style-type:"◆  ";}`}</style>
    <div className="mx-auto max-w-6xl space-y-5">
      <div className={contentApi ? "group-start-heading" : ""}><div><h1 className="text-3xl font-serif font-semibold">{contentApi ? "Notes from your publishing circle" : "Your Blog Spot"}</h1>{contentApi && <p>Behind the books, between the chapters, and from the voices in your group.</p>}</div></div>
      {groupPublished === false && <p>Your group is unpublished. Published posts become visible when you publish the group; drafts stay private.</p>}
      <p>Share the stories behind your books. Keep a draft for later, or publish a post for your readers.</p>
      <p role="status">{loading ? 'Loading your posts…' : busy || message || (dirty ? 'You have unsaved changes.' : '')}</p>
      <p role="alert" ref={errorRef} tabIndex={-1}>{error}</p>
      {!loading && !ready && <div><button className="underline min-h-[44px]" onClick={() => setRetry(value => value + 1)}>Retry loading</button>{!contentApi && <a className="underline ml-4" href="/loginauthor">Return to Author Login</a>}</div>}
      {ready && <>
        {pending.length > 0 && <div className="rounded-xl border border-bookish-purple/30 bg-white p-4"><p>An earlier update still needs to remove older public copies. Your current post status is saved.</p>{pending.map(item => <button key={item.id} disabled={!!busy} className="underline block min-h-[44px]" onClick={() => run('Finishing publication update…', async () => { const result = await api.resume(item); await refresh(); if (result.cleanupPending) throw new Error('The older copies could not all be removed. Please retry.'); setMessage('Publication update finished. Your edits are still here.'); })}>Finish update: {item.title}</button>)}</div>}
        {conflict && <button disabled={!!busy} className="underline min-h-[44px]" onClick={() => run('Reloading posts…', async () => { await refresh(); setConflict(false); setMessage('Posts reloaded. Copy any edits you want to keep, then select the post again to use its latest saved version.'); })}>Reload posts (keep current edits)</button>}
        <fieldset disabled={!!busy} className="min-w-0 space-y-5">
          <div className="rounded-xl border border-bookish-purple/15 bg-white/90 p-4 sm:p-6 shadow-xl">
            <h2 className="text-2xl font-serif font-semibold mb-4">{editing ? 'Edit Post' : 'Create New Post'}</h2>
            <label className="block mb-4">Post title<input type="text" maxLength={200} value={post.title} onChange={e => { setPost(previous => ({ ...previous, title: e.target.value })); setMessage(''); }} className="w-full min-h-[44px] px-4 py-2 border border-bookish-purple/30 rounded-md focus:ring-2 focus:ring-bookish-royal/40" /></label>
            <div role="group" aria-label="Post formatting" className="md:sticky md:top-24 z-10 mb-3 flex flex-wrap gap-2 rounded-md border border-bookish-purple/10 bg-white/95 p-2 shadow-sm">
              {tool('Bold', editor?.isActive('bold'), () => editor?.chain().focus().toggleBold().run(), 'B')}
              {tool('Italic', editor?.isActive('italic'), () => editor?.chain().focus().toggleItalic().run(), 'I')}
              {tool('Underline', editor?.isActive('underline'), () => editor?.chain().focus().toggleUnderline().run(), 'U')}
              {tool('Heading', editor?.isActive('heading', { level: 2 }), () => editor?.chain().focus().toggleHeading({ level: 2 }).run(), 'H2')}
              {tool('Bullet list', editor?.isActive('bulletList'), () => editor?.chain().focus().toggleBulletList().updateAttributes('bulletList', { listStyle: 'disc' }).run(), '• List')}
              {tool('Numbered list', editor?.isActive('orderedList'), () => editor?.chain().focus().toggleOrderedList().run(), '1. List')}
              {tool('Diamond list', editor?.isActive('bulletList', { listStyle: 'diamond' }), () => editor?.chain().focus().toggleBulletList().updateAttributes('bulletList', { listStyle: 'diamond' }).run(), '◆ List')}
              {tool('Text link', editor?.isActive('link'), () => setLink(), 'Link')}
              <label className="inline-flex items-center min-h-[44px] bg-bookish-purple text-white px-3 rounded-md cursor-pointer focus-within:ring-2 focus-within:ring-bookish-royal">Add Image<input aria-label="Add image" type="file" accept="image/png,image/jpeg,image/webp" className="sr-only" onChange={e => { const file = e.target.files?.[0]; e.target.value = ''; upload(file); }} /></label>
              <button type="button" className={toolbarButtonClass(false)} onClick={() => setLink(true)}>Image Link</button>
              <button type="button" className={toolbarButtonClass(false)} onClick={imageDescription}>Image Description</button>
            </div>
            <p className="text-sm mb-3">Images: PNG, JPEG, or WebP, up to 2 MB. Uploads are added to your post when you save.</p>
            <div className="blog-editor-content prose max-w-none border border-bookish-purple/30 rounded-md p-4 bg-bookish-cream/30"><EditorContent editor={editor} /></div>
            <div className="mt-6 flex flex-wrap gap-3">
              <button type="button" onClick={() => save(false)} className="min-h-[44px] border border-bookish-purple text-bookish-purple px-5 py-2 rounded-md">{editing && post.isPublished ? 'Unpublish and save draft' : 'Save draft'}</button>
              <button type="button" onClick={() => save(true)} className="min-h-[44px] bg-bookish-purple text-white px-5 py-2 rounded-md hover:bg-bookish-royal">{editing && post.isPublished ? 'Save published changes' : 'Publish now'}</button>
              <button type="button" className="underline min-h-[44px]" onClick={() => select(freshPost(), false)}>{editing ? 'Cancel editing / New post' : 'Clear post'}</button>
            </div>
          </div>
          <div className="bg-white/70 border border-bookish-purple/15 p-4 sm:p-6 rounded-xl shadow-xl"><h2><button type="button" aria-expanded={draftsOpen} className="font-serif text-xl min-h-[44px]" onClick={() => setDraftsOpen(value => !value)}>{draftsOpen ? '▾' : '▸'} Drafts ({drafts.length})</button></h2>{draftsOpen && (drafts.length ? list(drafts) : <p>No drafts yet. Start a story above and save it for later.</p>)}</div>
          <div className="bg-white/70 border border-bookish-purple/15 p-4 sm:p-6 rounded-xl shadow-xl"><h2 className="font-serif text-xl mb-4">Published posts</h2>{!published.length && <p>No published posts yet.</p>}{Object.keys(groups).sort().reverse().map(year => <div key={year}><h3><button type="button" aria-expanded={!!yearsOpen[year]} className="font-semibold min-h-[44px]" onClick={() => setYearsOpen(value => ({ ...value, [year]: !value[year] }))}>{yearsOpen[year] ? '▾' : '▸'} {year}</button></h3>{yearsOpen[year] && Object.keys(groups[year]).sort().reverse().map(month => { const key = year + '-' + month; const label = month === 'undated' ? 'Date unavailable' : new Date(Number(year), Number(month) - 1, 1).toLocaleString(undefined, { month: 'long' }); return <div className="pl-4" key={key}><h4><button type="button" aria-expanded={!!monthsOpen[key]} className="min-h-[44px]" onClick={() => setMonthsOpen(value => ({ ...value, [key]: !value[key] }))}>{monthsOpen[key] ? '▾' : '▸'} {label}</button></h4>{monthsOpen[key] && list(groups[year][month])}</div>; })}</div>)}</div>
        </fieldset>
      </>}
    </div>
  </section>;
};
export default Blog;
